import type { ColumnDef, Row } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/shared/ui/organisms/DataTable';

import type { TutorClassOption } from '../../../types/tutorAi';

export interface ClassTableProps {
  readonly classes: readonly TutorClassOption[];
  readonly isLoading: boolean;
  readonly onRowClick: (item: TutorClassOption) => void;
}

/**
 * Tabela de turmas do tutor.
 *
 * Duas colunas apenas — o lookup compartilhado devolve `key`/`value` e nenhum agregado.
 * Contagem de documentos e situação de indexação vivem no detalhe da turma, onde a
 * chamada que as traz já ia acontecer.
 *
 * Sem `Pagination`: o lookup não pagina, então a lista é o recorte da busca. Quem monta
 * avisa sobre o truncamento.
 */
export function ClassTable({ classes, isLoading, onRowClick }: ClassTableProps) {
  const { t } = useTranslation('tutorAI');

  const columns = useMemo<ColumnDef<TutorClassOption, unknown>[]>(
    () => [
      {
        accessorKey: 'label',
        header: () => <span className="normal-case">{t('classes.table.columns.class')}</span>,
        cell: ({ row }) => (
          <span className="text-ctx-content-title ts-heading-xxs">{row.original.label}</span>
        ),
      },
      {
        accessorKey: 'classStudentId',
        size: 140,
        header: () => <span className="normal-case">{t('classes.table.columns.id')}</span>,
        cell: ({ row }) => (
          <span className="tabular-nums text-ctx-content-placeholder ts-paragraph-xs">
            {row.original.classStudentId}
          </span>
        ),
      },
    ],
    [t],
  );

  const rowClickHandler = (row: Row<TutorClassOption>) => onRowClick(row.original);

  return (
    <DataTable
      data={classes as TutorClassOption[]}
      columns={columns}
      isLoading={isLoading}
      tableFixed
      onRowClick={rowClickHandler}
      emptyStateMessage={t('classes.table.empty')}
      emptyStateDescription={t('classes.table.emptyDescription')}
    />
  );
}
